/**
 * AdminWallets — ops dashboard listing all wallets, treasury balance,
 * and pending payout requests with approve/reject actions.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Wallet as WalletIcon, Coins, ShieldCheck, CheckCircle2, XCircle, ExternalLink, Mail, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

function money(n) { return `£${(Number(n) || 0).toFixed(2)}`; }

export default function AdminWallets() {
  const [wallets, setWallets] = useState([]);
  const [pending, setPending] = useState([]);
  const [processing, setProcessing] = useState([]);
  const [insights, setInsights] = useState({ daily: [], top_earners: [] });
  const [flags, setFlags] = useState([]);
  const [action, setAction] = useState(null);
  const [form, setForm] = useState({ bank_reference: "", admin_note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sendingStatements, setSendingStatements] = useState(false);

  const load = async () => {
    try {
      const [w, p, pr, ins, fl] = await Promise.all([
        api.get("/admin/wallets"),
        api.get("/admin/wallets/payouts?status=pending"),
        api.get("/admin/wallets/payouts?status=paid"),
        api.get("/admin/wallets/insights"),
        api.get("/admin/wallets/fraud-flags?resolved=false"),
      ]);
      setWallets(w.data.wallets || []);
      setPending(p.data.payouts || []);
      setProcessing(pr.data.payouts || []);
      setInsights(ins.data || { daily: [], top_earners: [] });
      setFlags(fl.data.flags || []);
    } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const sendMonthlyStatements = async () => {
    if (!window.confirm("Broadcast prior-month PDF statements to all users with wallet activity?")) return;
    setSendingStatements(true);
    try {
      const { data } = await api.post("/admin/wallets/send-monthly-statements");
      toast.success(`Statements sent · ${data.sent} delivered · ${data.failed} failed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setSendingStatements(false);
  };

  const resolveFlag = async (id) => {
    try {
      await api.post(`/admin/wallets/fraud-flags/${id}/resolve`);
      toast.success("Flag resolved");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const treasury = wallets.find(w => w.kind === "treasury");
  const nonTreasury = wallets.filter(w => w.kind !== "treasury");
  const totalUserBalance = nonTreasury.reduce((s, w) => s + w.balance_gbp, 0);

  const submitAction = async (e) => {
    e.preventDefault();
    if (!action) return;
    setSubmitting(true);
    try {
      const url = `/admin/wallets/payouts/${action.payout.id}/${action.mode}`;
      await api.post(url, form);
      toast.success(`Payout ${action.mode}ed`);
      setAction(null);
      setForm({ bank_reference: "", admin_note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="admin-wallets-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground overline">
            <WalletIcon className="w-4 h-4" /> Ops · Wallet Admin
          </div>
          <h1 className="text-3xl font-display font-light mt-1">Wallets & Payouts</h1>
        </div>
        <Button variant="outline" size="sm" onClick={sendMonthlyStatements} disabled={sendingStatements} data-testid="broadcast-statements-btn">
          <Mail className="w-3.5 h-3.5 mr-2" />
          {sendingStatements ? "Sending…" : "Email monthly statements"}
        </Button>
      </div>

      {/* Fraud alerts */}
      {flags.length > 0 && (
        <Card className="border-destructive/50 bg-destructive/5" data-testid="fraud-alerts-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <ShieldAlert className="w-4 h-4" /> {flags.length} fraud alert{flags.length !== 1 ? "s" : ""}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              {flags.map(f => (
                <div key={f.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`fraud-flag-${f.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{f._user?.email || f.user_id}</div>
                    <div className="text-xs text-destructive">{f.reason}</div>
                    <div className="text-[10px] text-muted-foreground">{new Date(f.created_at).toLocaleString("en-GB")}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => resolveFlag(f.id)} data-testid={`resolve-flag-${f.id}`}>
                    Resolve
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-primary text-primary-foreground">
          <CardContent className="pt-6">
            <div className="text-[10px] uppercase tracking-widest opacity-70">Treasury balance</div>
            <div className="text-3xl font-mono font-light mt-1" data-testid="treasury-balance">{money(treasury?.balance_gbp)}</div>
            <div className="text-xs opacity-70 mt-1">
              Lifetime in {money(treasury?.lifetime_credited_gbp)} · out {money(treasury?.lifetime_debited_gbp)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total user balances</div>
            <div className="text-3xl font-mono font-light mt-1" data-testid="user-balances-total">{money(totalUserBalance)}</div>
            <div className="text-xs text-muted-foreground mt-1">{nonTreasury.length} wallets</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Pending payouts</div>
            <div className="text-3xl font-mono font-light mt-1 text-amber-500" data-testid="pending-payouts-count">{pending.length}</div>
            <div className="text-xs text-muted-foreground mt-1">{money(pending.reduce((s, p) => s + p.amount_gbp, 0))} awaiting</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Paid out (all-time)</div>
            <div className="text-3xl font-mono font-light mt-1 text-emerald-500">{money(processing.reduce((s, p) => s + p.amount_gbp, 0))}</div>
            <div className="text-xs text-muted-foreground mt-1">{processing.length} disbursements</div>
          </CardContent>
        </Card>
      </div>

      {/* Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="insight-daily-payouts">
          <CardHeader><CardTitle className="text-sm">7-day payout volume</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-24">
              {insights.daily.map((d, i) => {
                const total = d.paid_gbp + d.pending_gbp;
                const max = Math.max(1, ...insights.daily.map(x => x.paid_gbp + x.pending_gbp));
                const h = Math.max(4, (total / max) * 90);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: £${total.toFixed(2)} (${d.count} req)`}>
                    <div className="w-full flex flex-col justify-end" style={{ height: 90 }}>
                      {d.pending_gbp > 0 && <div className="bg-amber-500 rounded-t" style={{ height: `${(d.pending_gbp / max) * 90}px` }} />}
                      {d.paid_gbp > 0 && <div className="bg-emerald-500" style={{ height: `${(d.paid_gbp / max) * 90}px` }} />}
                      {total === 0 && <div className="bg-muted rounded-t" style={{ height: 4 }} />}
                    </div>
                    <div className="text-[9px] text-muted-foreground">{d.date.slice(5)}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-3 mt-3 text-xs">
              <div className="flex items-center gap-1"><div className="w-2 h-2 bg-emerald-500" /> Paid</div>
              <div className="flex items-center gap-1"><div className="w-2 h-2 bg-amber-500" /> Pending</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="insight-top-earners">
          <CardHeader><CardTitle className="text-sm">Top earners (lifetime)</CardTitle></CardHeader>
          <CardContent>
            {insights.top_earners.length === 0
              ? <div className="text-xs text-muted-foreground text-center py-6">No earners yet.</div>
              : (
                <div className="space-y-2">
                  {insights.top_earners.map((w, i) => {
                    const max = insights.top_earners[0]?.lifetime_credited_gbp || 1;
                    const pct = Math.max(2, (w.lifetime_credited_gbp / max) * 100);
                    return (
                      <div key={w.id} className="text-sm">
                        <div className="flex justify-between items-baseline">
                          <span className="truncate">{i + 1}. {w._user?.name || w._user?.email}</span>
                          <span className="font-mono font-semibold text-emerald-500 shrink-0">{money(w.lifetime_credited_gbp)}</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending payouts ({pending.length})</TabsTrigger>
          <TabsTrigger value="wallets" data-testid="tab-wallets">All wallets ({nonTreasury.length})</TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-paid">History ({processing.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card>
            <CardContent className="pt-6">
              {pending.length === 0
                ? <div className="text-center py-8 text-muted-foreground text-sm">No pending payouts.</div>
                : (
                  <div className="divide-y divide-border">
                    {pending.map(p => (
                      <div key={p.id} className="py-3 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`pending-payout-${p.id}`}>
                        <div className="min-w-0">
                          <div className="font-mono font-semibold">{money(p.amount_gbp)}</div>
                          <div className="text-xs text-muted-foreground">
                            {p.user_email} · via {p.method.replace("_", " ")}
                            {p.bank_name && <> · {p.bank_name} ****{p.bank_account_last4}</>}
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            Requested {new Date(p.created_at).toLocaleString("en-GB")}
                          </div>
                          {p.note && <div className="text-xs italic mt-1 text-muted-foreground">"{p.note}"</div>}
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button size="sm" onClick={() => setAction({ payout: p, mode: "approve" })} data-testid={`approve-${p.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setAction({ payout: p, mode: "reject" })} data-testid={`reject-${p.id}`}>
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="wallets">
          <Card>
            <CardContent className="pt-6">
              <div className="divide-y divide-border">
                {nonTreasury.sort((a, b) => b.balance_gbp - a.balance_gbp).map(w => (
                  <div key={w.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`wallet-row-${w.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{w._user?.name || w._user?.email || w.user_id}</div>
                      <div className="text-xs text-muted-foreground">
                        {w._user?.email} · <span className="uppercase">{w._user?.role || "user"}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono font-semibold">{money(w.balance_gbp)}</div>
                      <div className="text-[10px] text-muted-foreground">in {money(w.lifetime_credited_gbp)} · out {money(w.lifetime_debited_gbp)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="paid">
          <Card>
            <CardContent className="pt-6">
              <div className="divide-y divide-border">
                {processing.map(p => (
                  <div key={p.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium">{money(p.amount_gbp)} · {p.user_email}</div>
                      <div className="text-xs text-muted-foreground">
                        Approved {new Date(p.approved_at || p.created_at).toLocaleString("en-GB")}
                        {p.bank_reference && <> · Ref {p.bank_reference}</>}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-emerald-500 border-emerald-500/40">PAID</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approve/reject modal */}
      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{action?.mode === "approve" ? "Approve payout" : "Reject payout"}</DialogTitle>
          </DialogHeader>
          {action && (
            <form onSubmit={submitAction} className="space-y-3">
              <div className="text-sm text-muted-foreground">
                <div><b>{money(action.payout.amount_gbp)}</b> to <b>{action.payout.user_email}</b></div>
                <div className="text-xs mt-1">Method: {action.payout.method.replace("_", " ")}</div>
              </div>
              {action.mode === "approve" && (
                <div>
                  <Label>Bank reference (optional)</Label>
                  <Input value={form.bank_reference} onChange={(e) => setForm(f => ({ ...f, bank_reference: e.target.value }))} placeholder="TC-2026-08-001" data-testid="approve-ref" />
                </div>
              )}
              <div>
                <Label>Note {action.mode === "reject" && <span className="text-destructive">*</span>}</Label>
                <Textarea rows={2} value={form.admin_note} onChange={(e) => setForm(f => ({ ...f, admin_note: e.target.value }))} data-testid="admin-note" required={action.mode === "reject"} />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting} variant={action.mode === "approve" ? "default" : "destructive"} data-testid="confirm-action-btn">
                  {submitting ? "Working…" : `Confirm ${action.mode}`}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
